import { useState, useMemo } from 'react';
import { PickUpFilters } from '../models';
import { useQueryParams } from '@/hooks';
import dayjs from 'dayjs';

const PICKUP_FILTERS_STORAGE_KEY = 'BUSINESS-PICKUP-FILTERS';

const readStoredFilters = (): PickUpFilters => {
  try {
    const raw = localStorage.getItem(PICKUP_FILTERS_STORAGE_KEY);
    if (!raw) return {};

    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const getString = (key: keyof PickUpFilters) =>
      typeof parsed[key] === 'string' ? (parsed[key] as string) : undefined;

    return {
      createdAtFrom: getString('createdAtFrom'),
      createdAtTo: getString('createdAtTo'),
      visitedAtFrom: getString('visitedAtFrom'),
      visitedAtTo: getString('visitedAtTo'),
      status: getString('status'),
      commerceId: getString('commerceId'),
      driverId: getString('driverId'),
    };
  } catch {
    return {};
  }
};

export function usePickUpFilter() {
  const { getQueryParam, setQueryParams } = useQueryParams();

  const initialFilter: PickUpFilters = useMemo(
    () => ({
      createdAtFrom: dayjs().subtract(15, 'day').startOf('day').toISOString(),
      createdAtTo: dayjs().add(1, 'day').endOf('day').toISOString(),
      visitedAtFrom: undefined,
      visitedAtTo: undefined,
      status: undefined,
      commerceId: undefined,
      driverId: undefined,
    }),
    []
  );

  const [filters, setFilters] = useState<PickUpFilters>(() => {
    const stored = readStoredFilters();
    const urlParams = new URLSearchParams(window.location.search);
    const resolveValue = (
      key: keyof PickUpFilters,
      fallback?: string
    ): string | undefined =>
      urlParams.has(key)
        ? getQueryParam(key) || undefined
        : stored[key] || fallback;

    return {
      createdAtFrom: resolveValue(
        'createdAtFrom',
        initialFilter.createdAtFrom
      ),
      createdAtTo: resolveValue('createdAtTo', initialFilter.createdAtTo),
      visitedAtFrom: resolveValue(
        'visitedAtFrom',
        initialFilter.visitedAtFrom
      ),
      visitedAtTo: resolveValue('visitedAtTo', initialFilter.visitedAtTo),
      status: resolveValue('status', initialFilter.status),
      commerceId: resolveValue('commerceId', initialFilter.commerceId),
      driverId: resolveValue('driverId', initialFilter.driverId),
    };
  });

  const updateFilters = (newFilters: PickUpFilters) => {
    setFilters(newFilters);
    localStorage.setItem(
      PICKUP_FILTERS_STORAGE_KEY,
      JSON.stringify(newFilters)
    );
    setQueryParams({
      createdAtFrom: newFilters.createdAtFrom || undefined,
      createdAtTo: newFilters.createdAtTo || undefined,
      visitedAtFrom: newFilters.visitedAtFrom || undefined,
      visitedAtTo: newFilters.visitedAtTo || undefined,
      status: newFilters.status || undefined,
      commerceId: newFilters.commerceId || undefined,
      driverId: newFilters.driverId || undefined,
      page: '1',
    });
  };

  return {
    filters,
    setFilters: updateFilters,
    initialFilter,
  };
}

export default usePickUpFilter;
